/**
 * KpiStrip — the Book screen's headline numbers (chat-centric revamp §4).
 *
 * One row answers "where do I stand": the book's total value in base
 * currency, the day-Δ beside it with its sign and color, and the two P/L
 * figures under it. When the day-Δ had to exclude symbols (stale prices,
 * spec §6.8), a footnote names them — a silent omission would let the
 * reader mistake a partial sum for the whole book's move.
 *
 * Unrealized/realized are summed from the positions payload (the same data
 * the Positions sub-tab renders); the dashboard endpoint itself does not
 * carry P/L totals. The backend computes those figures in each instrument's
 * LOCAL currency, so they are FX-normalized through the portfolio total's
 * implied rates (utils/currencyRates — the same conversion the positions
 * table's allocation column uses) before summing: a 5,000 HKD gain beside a
 * 100 USD one must read +740, not +5,100.
 */

import React, { useMemo } from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortfolioTotal } from '../../services/api/queries/usePortfolio';
import { buildCurrencyRateMap, rateForCurrency } from '../../utils/currencyRates';
import type { DashboardData } from '../../services/dashboardApi';
import type { Position } from '../../services/portfolioApi';

interface KpiStripProps {
  data: DashboardData | null;
  positions: Position[];
}

function formatAmount(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Explicit sign, so a positive day-Δ never reads as a bare number. */
function formatSigned(value: number): string {
  return `${value > 0 ? '+' : ''}${formatAmount(value)}`;
}

export function KpiStrip({ data, positions }: KpiStripProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Implied FX rates from the portfolio total — the same breakdown the
  // positions table derives its allocation conversion from.
  const { data: portfolioTotal } = usePortfolioTotal();
  const currencyRates = useMemo(
    () => buildCurrencyRateMap(portfolioTotal?.currency_breakdown),
    [portfolioTotal]
  );

  // P/L arrives in instrument-local currency; convert each figure to base
  // before it enters the sum.
  const totals = useMemo(() => {
    return positions.reduce(
      (acc, position) => {
        const rate = rateForCurrency(currencyRates, position.currency);
        return {
          unrealized: acc.unrealized + Number(position.unrealized_pl ?? 0) * rate,
          realized: acc.realized + Number(position.realized_pl ?? 0) * rate,
        };
      },
      { unrealized: 0, realized: 0 }
    );
  }, [positions, currencyRates]);

  const dayChange = data?.day_change_base ?? null;
  const excluded = data?.excluded_symbols ?? [];
  const dayDeltaColor =
    dayChange === null || dayChange === 0
      ? theme.colors.textSecondary
      : dayChange > 0
        ? theme.colors.success
        : theme.colors.error;

  return (
    <View style={styles.card}>
      <View style={styles.valueRow}>
        <Text style={styles.totalValue} testID="kpi-total-value">
          {data
            ? `${data.base_currency} ${Math.round(data.total_value_base_currency).toLocaleString()}`
            : '—'}
        </Text>
        {dayChange !== null && (
          <Text
            style={[styles.dayDelta, { color: dayDeltaColor }]}
            testID={dayChange > 0 ? 'kpi-day-delta-positive' : 'kpi-day-delta-negative'}
          >
            {`${formatSigned(dayChange)} ${data?.base_currency ?? ''}`.trim()}
          </Text>
        )}
      </View>

      <View style={styles.plRow}>
        <View style={styles.plItem}>
          <Text style={styles.plLabel}>{t('portfolio.unrealizedPL')}</Text>
          <Text
            style={[
              styles.plValue,
              {
                color:
                  totals.unrealized > 0
                    ? theme.colors.success
                    : totals.unrealized < 0
                      ? theme.colors.error
                      : theme.colors.textSecondary,
              },
            ]}
          >
            {formatSigned(totals.unrealized)}
          </Text>
        </View>
        <View style={styles.plItem}>
          <Text style={styles.plLabel}>{t('portfolio.realizedPL')}</Text>
          <Text
            style={[
              styles.plValue,
              {
                color:
                  totals.realized > 0
                    ? theme.colors.success
                    : totals.realized < 0
                      ? theme.colors.error
                      : theme.colors.textSecondary,
              },
            ]}
          >
            {formatSigned(totals.realized)}
          </Text>
        </View>
      </View>

      {excluded.length > 0 && (
        <Text style={styles.excludedNote} testID="kpi-excluded-note">
          {t('book.dayChangeExcludes', {
            count: excluded.length,
            symbols: excluded.join(', '),
          })}
        </Text>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      padding: theme.spacing.lg,
      marginHorizontal: theme.spacing.lg,
      marginTop: theme.spacing.md,
    },
    valueRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
    },
    totalValue: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    dayDelta: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    plRow: {
      flexDirection: 'row',
      gap: theme.spacing.xl,
      marginTop: theme.spacing.sm,
    },
    plItem: { gap: 2 },
    plLabel: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    plValue: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    excludedNote: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
      marginTop: theme.spacing.sm,
    },
  });
}
